import { z } from 'zod';
import { EventName } from '../ingest/common.ts';
import { PropertyCondition } from './triggers.ts';

/**
 * Conversion events of a campaign (BB-103, AN-003): up to four actions counted after a profile got the message —
 * from receipt (push accepted by the provider, card or in-app impression), from assignment for the control group,
 * from entry for a Canvas. The first is primary. Each counts at most once per send, within its own window.
 * Purchases are left out: VCG does not send them.
 */
export const CONVERSIONS_MAX = 4;
export const CONVERSION_WINDOW_MAX_SECONDS = 2_592_000;
export const CONVERSION_WINDOW_DEFAULT_SECONDS = 3 * 86_400;

const window = z.number().int().min(60).max(CONVERSION_WINDOW_MAX_SECONDS);

export const ConversionEvent = z.discriminatedUnion('kind', [
  z.strictObject({ id: z.uuid(), kind: z.literal('custom_event'), event: EventName, propertyFilters: z.array(PropertyCondition).max(10).default([]), windowSeconds: window }),
  z.strictObject({ id: z.uuid(), kind: z.literal('session_start'), windowSeconds: window }),
]);
export type ConversionEvent = z.infer<typeof ConversionEvent>;

export const Conversions = z
  .array(ConversionEvent)
  .max(CONVERSIONS_MAX)
  .refine((list) => new Set(list.map((c) => c.id)).size === list.length, 'conversion ids must be unique');
export type Conversions = z.infer<typeof Conversions>;

/** Used when a campaign sets none: a session start within three days, like Braze's default. */
export const DEFAULT_CONVERSION_ID = '00000000-0000-5000-8000-000000000c01';
export const DEFAULT_CONVERSIONS: Conversions = [{ id: DEFAULT_CONVERSION_ID, kind: 'session_start', windowSeconds: CONVERSION_WINDOW_DEFAULT_SECONDS }];

/** Conversions of one campaign variant (or the control group): sends counted and profiles that converted. */
export interface ConversionSummaryRow {
  variantApiId: string;
  isControl: boolean;
  recipients: number;
  /** Unique converted profiles per conversion id; the primary conversion is the first of the campaign. */
  converted: Record<string, number>;
}
